import { CssBaseline, PaletteMode } from '@mui/material';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ColorMode, createAppTheme } from '@shared/theme/mui';
import { portalPreset } from '@shared/theme/mui/preset';
import {
  ColorModeContext,
  ColorModeContextType,
  MuiColorMode,
  toColorMode,
  toMuiMode,
} from '@shared/theme/providers/ColorModeContext';

import { useSdk } from 'component/hooks/useSdk';
import { useSDKPaletteMode } from 'component/postMessage/SDKPaletteModeContext';
import { useAppMode } from 'component/root/AppModeProvider';

const PRELOCK_MODE_STORAGE_KEY = 'mui-mode-prelock';

const isMuiMode = (mode: unknown): mode is MuiColorMode =>
  mode === 'light' || mode === 'dark' || mode === 'system';

const toPaletteMode = (mode: unknown): PaletteMode | null =>
  mode === 'light' || mode === 'dark' ? mode : null;

const readPrelockMode = () => {
  const storedMode = window.localStorage.getItem(PRELOCK_MODE_STORAGE_KEY);
  if (storedMode === 'auto') {
    return 'system';
  }

  return isMuiMode(storedMode) ? storedMode : null;
};

const writePrelockMode = (mode: MuiColorMode) => {
  window.localStorage.setItem(PRELOCK_MODE_STORAGE_KEY, mode);
};

const clearPrelockMode = () => {
  window.localStorage.removeItem(PRELOCK_MODE_STORAGE_KEY);
};

export const AutoThemeProvider = ({ children }: PropsWithChildren) => {
  const { isSdk } = useAppMode();

  const theme = useMemo(() => {
    return createAppTheme(portalPreset, { isSdk });
  }, [isSdk]);

  return (
    <ThemeProvider theme={theme} defaultMode="system" modeStorageKey="mui-mode">
      <CssBaseline />
      <ModeController>{children}</ModeController>
    </ThemeProvider>
  );
};

const ModeController = ({ children }: PropsWithChildren) => {
  const { mode, setMode, systemMode, colorScheme } = useColorScheme();
  const sdk = useSdk();
  const sdkPaletteMode = useSDKPaletteMode();
  const sdkOverride = sdk?.theme || sdkPaletteMode || null;

  const [userMode, setUserMode] = useState<MuiColorMode>(() => readPrelockMode() ?? 'system');
  const [lockCount, setLockCount] = useState(0);

  const userModeRef = useRef<MuiColorMode>(userMode);
  const lockCountRef = useRef(0);
  const preLockModeRef = useRef<MuiColorMode | null>(null);
  const lockPinnedModeRef = useRef<PaletteMode | null>(null);
  const preOverrideModeRef = useRef<MuiColorMode | null>(null);
  const sdkOverrideRef = useRef<PaletteMode | null>(sdkOverride);
  const recoveredPrelockRef = useRef(false);

  const resolvedMode = sdkOverride || toPaletteMode(colorScheme) || systemMode || 'light';
  const resolvedModeRef = useRef(resolvedMode);

  useEffect(() => {
    userModeRef.current = userMode;
  }, [userMode]);

  useEffect(() => {
    sdkOverrideRef.current = sdkOverride;
  }, [sdkOverride]);

  useEffect(() => {
    resolvedModeRef.current = resolvedMode;
  }, [resolvedMode]);

  useEffect(() => {
    if (recoveredPrelockRef.current) {
      return;
    }

    recoveredPrelockRef.current = true;
    const prelockMode = readPrelockMode();
    if (!prelockMode) {
      return;
    }

    if (lockCountRef.current > 0) {
      return;
    }

    setMode(prelockMode);
    clearPrelockMode();
  }, [setMode]);

  useEffect(() => {
    if (sdkOverride) {
      if (preOverrideModeRef.current === null) {
        preOverrideModeRef.current = userModeRef.current;
      }

      setMode(sdkOverride);
      return;
    }

    const preOverrideMode = preOverrideModeRef.current;
    if (preOverrideMode === null) {
      return;
    }

    preOverrideModeRef.current = null;
    userModeRef.current = preOverrideMode;
    setUserMode(preOverrideMode);

    if (lockCountRef.current > 0) {
      setMode(lockPinnedModeRef.current || resolvedModeRef.current);
      return;
    }

    setMode(preOverrideMode);
  }, [sdkOverride, setMode]);

  useEffect(
    () => () => {
      const modeToRestore = preLockModeRef.current || preOverrideModeRef.current;
      if (modeToRestore) {
        setMode(modeToRestore);
      }

      clearPrelockMode();
    },
    [setMode]
  );

  const setColorMode = useCallback(
    (mode: ColorMode) => {
      if (lockCountRef.current > 0 || sdkOverride) {
        return;
      }

      const muiMode = toMuiMode(mode);
      userModeRef.current = muiMode;
      setUserMode(muiMode);
      setMode(muiMode);
    },
    [sdkOverride, setMode]
  );

  const lockThemeChanges = useCallback(() => {
    if (lockCountRef.current === 0 && preLockModeRef.current === null) {
      const pinnedMode = resolvedModeRef.current;
      preLockModeRef.current = userModeRef.current;
      lockPinnedModeRef.current = pinnedMode;
      writePrelockMode(userModeRef.current);
      setMode(pinnedMode);
    }

    lockCountRef.current += 1;
    setLockCount(lockCountRef.current);
  }, [setMode]);

  const unlockThemeChanges = useCallback(() => {
    lockCountRef.current = Math.max(0, lockCountRef.current - 1);

    if (lockCountRef.current === 0) {
      const preLockMode = preLockModeRef.current;
      preLockModeRef.current = null;
      lockPinnedModeRef.current = null;
      clearPrelockMode();

      if (preLockMode) {
        userModeRef.current = preLockMode;
        setUserMode(preLockMode);

        if (!sdkOverrideRef.current) {
          setMode(preLockMode);
        }
      }
    }

    setLockCount(lockCountRef.current);
  }, [setMode]);

  const isThemeChangeLocked = lockCount > 0 || Boolean(sdkOverride);

  const contextValue = useMemo<ColorModeContextType>(
    () => ({
      colorMode: toColorMode(isThemeChangeLocked ? userMode : mode || 'system'),
      resolvedMode,
      setColorMode,
      lockThemeChanges,
      unlockThemeChanges,
      isThemeChangeLocked,
    }),
    [
      isThemeChangeLocked,
      userMode,
      mode,
      resolvedMode,
      setColorMode,
      lockThemeChanges,
      unlockThemeChanges,
    ]
  );

  return <ColorModeContext.Provider value={contextValue}>{children}</ColorModeContext.Provider>;
};
